'use client';

import { useState, useMemo, useCallback } from 'react';
import { Box, Typography, ButtonBase, CircularProgress } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import IconPressable from '@/components/IconPressable';
import { useAuthStore } from '@/stores/authStore';
import { getCollection, getPieceBalance, exchangePiece } from '@/api/feedApi';
import { useForcedBlur, FORCED_BLUR_SRC } from '@/hooks/useForcedBlur';
import { getAssetUrl } from '@/utils/assetUrl';
import { APP_MAX_WIDTH } from '@/components/PageContent/PageContent';
import Portal from '@/components/Portal/Portal';
import { useToastStore } from '@/stores/toastStore';
import { getErrorMessage } from '@/utils/errorMessages';
import { getLocalizedText } from '@/utils/getLocalizedText';
import { useAppLanguage } from '@/lang';
import { useTranslator } from '@/lang';
import { Icon } from '@/components/icons';
import {
  TEMP_FG_PRIMARY,
  TEMP_FG_PRIMARY_LOW,
  TEMP_FG_ACCENT,
  TEMP_BG_INTIMACY_HIGH,
} from '@/constants/tempColors';
import { useDarkColors } from '@/hooks/useDarkColors';
import {
  TEMP_TEXT_BOLD_M,
  TEMP_TEXT_BOLD_2XS,
  TEMP_TEXT_BOLD_XXS,
  TEMP_TEXT_MEDIUM_XS,
  TEMP_TEXT_MEDIUM_M,
  TEMP_TEXT_SEMIBOLD_XS,
} from '@/constants/tempTypo';
import type { CharacterCollectionDto, CollectionAssetDto, INTIMACY_CONDITION } from '@/shared/types/api/Api';

/** 등급 우선순위: L → U → S → R → N */
const GRADE_ORDER: Record<string, number> = { L: 0, U: 1, S: 2, R: 3, N: 4 };

/** INTIMACY_CONDITION → 레벨 숫자 (LEVEL0=0, LEVEL1=1, ..., LEVEL10=10) */
function getIntimacyLevel(condition: INTIMACY_CONDITION | string): number {
  const m = condition.match(/LEVEL(\d+)/);
  return m ? parseInt(m[1], 10) : 0;
}

/** 비디오 에셋의 포스터(썸네일) 경로 생성 */
function getVideoPosterPath(assetPath: string): string {
  const lastDot = assetPath.lastIndexOf('.');
  const basePath = lastDot > 0 ? assetPath.substring(0, lastDot) : assetPath;
  return `${basePath}_poster.webp`;
}

/** 정렬: 수집된 것 먼저, 그 안에서 등급 높은 순 */
function sortAssets(assets: CollectionAssetDto[]): CollectionAssetDto[] {
  return [...assets].sort((a, b) => {
    const aCollected = a.isOwned || a.isUnlocked ? 0 : 1;
    const bCollected = b.isOwned || b.isUnlocked ? 0 : 1;
    if (aCollected !== bCollected) return aCollected - bCollected;
    return (GRADE_ORDER[a.grade] ?? 99) - (GRADE_ORDER[b.grade] ?? 99);
  });
}

export default function MyCollectionTab() {
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  const { language } = useAppLanguage();
  const { t, f } = useTranslator();
  const c = useDarkColors();
  const [selectedCharacter, setSelectedCharacter] = useState<CharacterCollectionDto | null>(null);

  const { data: collection, isLoading } = useQuery({
    queryKey: ['collection-detail'],
    queryFn: getCollection,
    enabled: isLoggedIn,
  });

  const totalAssets = collection?.totalAssets ?? 0;
  const totalCollected = useMemo(() => {
    if (!collection?.characters) return 0;
    return collection.characters.reduce((sum, char) => {
      return sum + char.assets.filter((a) => a.isOwned || a.isUnlocked).length;
    }, 0);
  }, [collection?.characters]);
  const collectedPercent = totalAssets > 0 ? Math.round((totalCollected / totalAssets) * 100) : 0;

  if (!isLoggedIn) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          gap: '12px',
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            bgcolor: c.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name="icLock" size={28} color={c.fgLower} />
        </Box>
        <Typography sx={{ ...TEMP_TEXT_MEDIUM_M, color: c.fgLow }}>
          {t('mypage.login_required')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      {/* Collection Progress */}
      <Box sx={{ px: '20px', py: '16px', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '10px' }}>
          <Typography sx={{ ...TEMP_TEXT_BOLD_M, color: c.fg }}>
            {t('collection.collect_rate')}
          </Typography>
          <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XS, color: c.fgLow }}>
            {totalCollected} / {totalAssets} ({collectedPercent}%)
          </Typography>
        </Box>
        <Box
          sx={{
            width: '100%',
            height: 6,
            borderRadius: 3,
            bgcolor: c.isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)',
            overflow: 'hidden',
          }}
        >
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${collectedPercent}%` }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            style={{
              height: '100%',
              borderRadius: 3,
              background: `linear-gradient(90deg, ${TEMP_FG_PRIMARY} 0%, #8b48ff 100%)`,
            }}
          />
        </Box>
      </Box>

      {/* Scrollable Character Sections — 세로 스크롤 */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          minHeight: 0,
        }}
      >
        <Box sx={{ pb: '20px', display: 'flex', flexDirection: 'column', gap: '28px' }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
              <CircularProgress size={36} sx={{ color: TEMP_FG_PRIMARY }} />
            </Box>
          ) : collection?.characters.map((character) => (
            <CharacterSection
              key={character.characterId}
              character={character}
              language={language}
              onViewAll={() => setSelectedCharacter(character)}
            />
          ))}

          {!isLoading && (!collection?.characters || collection.characters.length === 0) && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 200,
                gap: '12px',
              }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  bgcolor: c.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon name="icTrophy" size={28} color={c.fgLower} />
              </Box>
              <Typography sx={{ ...TEMP_TEXT_MEDIUM_M, color: c.fgLow }}>
                {t('collection.no_items')}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* 전체보기 바텀시트 — body portal (스택 화면 transform·스크롤 컨테이너에
          갇히면 iOS에서 페이지 하단 버튼이 시트 위에 그려짐) */}
      <Portal>
        <AnimatePresence>
          {selectedCharacter && (
            <CollectionDetailSheet
              character={selectedCharacter}
              language={language}
              onClose={() => setSelectedCharacter(null)}
            />
          )}
        </AnimatePresence>
      </Portal>
    </Box>
  );
}

/* ─── 컬렉션 전체보기 바텀시트 ─── */

function CollectionDetailSheet({
  character,
  language,
  onClose,
}: {
  character: CharacterCollectionDto;
  language: string;
  onClose: () => void;
}) {
  const { t, f } = useTranslator();
  const c = useDarkColors();
  const name = getLocalizedText(character.characterName, language);
  const sortedAssets = sortAssets(character.assets);
  const collectedCount = sortedAssets.filter((a) => a.isOwned || a.isUnlocked).length;
  const [exchangeAsset, setExchangeAsset] = useState<CollectionAssetDto | null>(null);

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1700, maxWidth: APP_MAX_WIDTH, margin: '0 auto' }}
      />
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: c.isDark ? '#0d0d0d' : '#ffffff',
          zIndex: 1701,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          maxHeight: '90vh',
          maxWidth: APP_MAX_WIDTH,
          margin: '0 auto',
        }}
      >
        {/* X 닫기 */}
        <Box sx={{ width: '100%', height: 64, display: 'flex', alignItems: 'center', py: '14px', flexShrink: 0 }}>
          <Box sx={{ pl: '20px', pr: '4px', py: '6px' }}>
            <IconPressable name="icClose" size={24} color={c.fg} onPress={onClose} />
          </Box>
        </Box>

        {/* 프로필 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
          <Box sx={{ width: 90, height: 90, borderRadius: '50%', overflow: 'hidden' }}>
            {character.profileImagePath ? (
              <Box component="img" src={getAssetUrl(character.profileImagePath, 'large')} alt={name} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <Box sx={{ width: '100%', height: '100%', bgcolor: c.bgInset }} />
            )}
          </Box>
          <Typography sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.4, color: c.fg, mt: '14px' }}>
            {name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', bgcolor: c.isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)', borderRadius: '9999px', px: '12px', py: '6px', mt: '10px' }}>
            <Icon name="icTrophy" size={18} color="#F3B160" />
            <Typography sx={{ fontSize: 15, fontWeight: 600, lineHeight: 1.4, color: c.fg }}>
              {collectedCount} / {character.totalCount}
            </Typography>
          </Box>
        </Box>

        {/* 에셋 그리드 (3열) */}
        <Box sx={{ flex: 1, overflow: 'auto', width: '100%', px: '20px', pb: '20px', minHeight: 0, mt: '20px' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 102px)', gap: '12px', justifyContent: 'center' }}>
            {sortedAssets.map((asset) => (
              <AssetCard key={asset.id} asset={asset} size={102} isOfficialMuse={character.isOfficialMuse} onLockedClick={setExchangeAsset} />
            ))}
          </Box>
        </Box>
      </motion.div>

      {/* 조각 교환 모달 */}
      <AnimatePresence>
        {exchangeAsset && (
          <PieceExchangeModal
            asset={exchangeAsset}
            onClose={() => setExchangeAsset(null)}
            onSuccess={() => {
              setExchangeAsset(null);
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
}

function CharacterSection({
  character,
  language,
  onViewAll,
}: {
  character: CharacterCollectionDto;
  language: string;
  onViewAll: () => void;
}) {
  const { t, f } = useTranslator();
  const c = useDarkColors();
  const name = getLocalizedText(character.characterName, language);
  const prompt = character.collectionPrompt
    ? getLocalizedText(character.collectionPrompt, language)
    : f(t('collection.fallback_prompt'), [name]);

  const sortedAssets = useMemo(() => sortAssets(character.assets), [character.assets]);
  const collectedCount = sortedAssets.filter((a) => a.isOwned || a.isUnlocked).length;

  return (
    <Box sx={{ width: '100%' }}>
      {/* Character Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: '12px',
          px: '20px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              overflow: 'hidden',
              bgcolor: c.bgInset,
              flexShrink: 0,
            }}
          >
            {character.profileImagePath && (
              <Box
                component="img"
                src={getAssetUrl(character.profileImagePath, 'large')}
                alt={name}
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            )}
          </Box>
          <Typography
            sx={{
              ...TEMP_TEXT_BOLD_M,
              color: c.fg,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {prompt}
          </Typography>
          <Typography sx={{ ...TEMP_TEXT_MEDIUM_XS, color: c.fgLower, flexShrink: 0 }}>
            ({collectedCount}/{character.totalCount})
          </Typography>
        </Box>
        <ButtonBase onClick={onViewAll} sx={{ py: '4px', pl: '8px', flexShrink: 0 }}>
          <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XS, color: c.fgLow }}>
            {t('collection.view_all')}
          </Typography>
          <Icon name="icChevronRight" size={16} color={c.fgLow} />
        </ButtonBase>
      </Box>

      {/* 가로 스크롤 에셋 리스트 */}
      <div
        style={{
          display: 'flex',
          gap: 10,
          overflowX: 'auto',
          overflowY: 'hidden',
          paddingLeft: 20,
          paddingRight: 20,
          paddingBottom: 4,
          scrollbarWidth: 'none',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {sortedAssets.map((asset) => (
          <AssetCard key={asset.id} asset={asset} isOfficialMuse={character.isOfficialMuse} />
        ))}
      </div>
    </Box>
  );
}

const GRADE_BORDER: Record<string, { gradient: string; shadow?: string }> = {
  L: { gradient: 'linear-gradient(180deg, #FFE76F 0%, #8000FF 100%)', shadow: '0 0 10px rgba(128,0,255,0.5)' },
  U: { gradient: 'linear-gradient(180deg, #DDB139 0%, #462300 100%)' },
  S: { gradient: 'linear-gradient(180deg, #DADADA 0%, #5A5A5A 100%)' },
  R: { gradient: 'linear-gradient(180deg, #4777FF 0%, #0F46E4 100%)' },
  N: { gradient: 'linear-gradient(180deg, #834D10 0%, #5F390F 100%)' },
};

function AssetCard({
  asset,
  size = 80,
  isOfficialMuse,
  onLockedClick,
}: {
  asset: CollectionAssetDto;
  size?: number;
  isOfficialMuse: boolean;
  onLockedClick?: (asset: CollectionAssetDto) => void;
}) {
  const { t } = useTranslator();
  const c = useDarkColors();
  const forcedBlur = useForcedBlur();
  const isUsed = asset.isUnlocked;
  const isOwned = asset.isOwned && !asset.isUnlocked;
  const isLocked = !asset.isOwned && !asset.isUnlocked;
  const isGachaItem = !isOfficialMuse && (!!asset.itemId || asset.pieceCost > 0);
  const intimacyLevel = getIntimacyLevel(asset.intimacyCondition);

  // 해금된(=isUnlocked) 아이템은 forcedBlur(심사용/IAP)이 켜져있어도 실제 이미지를 노출
  const imgSrc = (forcedBlur && !asset.isUnlocked)
    ? FORCED_BLUR_SRC
    : asset.assetPath
    ? (asset.assetType === 'VIDEO'
        ? getAssetUrl(getVideoPosterPath(asset.assetPath), 'thumb')
        : getAssetUrl(asset.assetPath, 'thumb'))
    : '/images/blur.png';

  const handleClick = () => {
    if (isLocked && asset.pieceCost > 0 && onLockedClick) {
      onLockedClick(asset);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center', flexShrink: 0, cursor: isLocked && asset.pieceCost > 0 ? 'pointer' : 'default' }} onClick={handleClick}>
      <div style={{
        position: 'relative', width: size, height: size, overflow: 'hidden',
        borderRadius: '10px',
        ...(isGachaItem
          ? { background: isLocked ? '#555' : (GRADE_BORDER[asset.grade]?.gradient || '#555'), padding: 4 }
          : {}),
        ...(asset.grade === 'L' && !isLocked && isGachaItem ? { boxShadow: GRADE_BORDER.L.shadow } : {}),
      }}>
        <div style={{
          width: '100%', height: '100%',
          borderRadius: isGachaItem ? '7px' : '10px',
          overflow: 'hidden',
          backgroundColor: c.isDark ? '#1a1a1a' : '#e8e8e8',
        }}>
          <img
            src={imgSrc}
            alt=""
            onError={(e) => { (e.target as HTMLImageElement).src = '/images/blur.png'; }}
            style={{
              width: '100%', height: '100%', objectFit: 'cover', display: 'block',
              ...(isLocked && { filter: 'brightness(0.3) blur(20px)' }),
              ...(isOwned && { filter: 'blur(6px)' }),
            }}
          />
        </div>
        {isGachaItem && !isLocked && (
          <img
            src={`/assets/items/${asset.grade}.png`}
            alt=""
            style={{ position: 'absolute', top: 2, left: 2, height: 42, width: 'auto', zIndex: 2, pointerEvents: 'none', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.6))' }}
          />
        )}

        {/* 친밀도 배지 (아이템형이 아닌 에셋) */}
        {!isGachaItem && intimacyLevel > 0 && (
          <Box sx={{
            position: 'absolute', top: 4, left: 4, zIndex: 3,
            bgcolor: intimacyLevel >= 9 ? TEMP_BG_INTIMACY_HIGH : TEMP_FG_PRIMARY_LOW,
            borderRadius: '9999px', px: '5px', py: '1px', height: 18,
            display: 'flex', alignItems: 'center', gap: '2px',
          }}>
            <Icon name="icHeartFilled" size={9} color={intimacyLevel >= 9 ? TEMP_FG_ACCENT : TEMP_FG_PRIMARY} />
            <Typography sx={{ ...TEMP_TEXT_BOLD_XXS, color: intimacyLevel >= 9 ? TEMP_FG_ACCENT : TEMP_FG_PRIMARY }}>
              {intimacyLevel * 10}°C
            </Typography>
          </Box>
        )}

        {/* 자물쇠 */}
        {(isOwned || isLocked) && (
          <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 2 }}>
            <Icon name="icLock" size={isLocked ? 28 : 20} color="rgba(255,255,255,0.7)" />
          </Box>
        )}

        {/* 보상 아이콘 (획득완료) */}
        {isOwned && (
          <Box sx={{ position: 'absolute', bottom: 4, left: 4, zIndex: 2, display: 'flex', gap: '2px', alignItems: 'center' }}>
            {asset.assetType === 'IMAGE' && <Icon name="icImage" size={12} color="rgba(255,255,255,0.8)" />}
            {asset.assetType === 'VIDEO' && <Icon name="icPlayFill" size={12} color="rgba(255,255,255,0.8)" />}
            {asset.voicePath && (
              <Box sx={{ bgcolor: '#0d0d0d', borderRadius: '50%', p: '2px', display: 'flex' }}>
                <Icon name="icVolume" size={8} color="rgba(255,255,255,0.8)" />
              </Box>
            )}
          </Box>
        )}
      </div>

      {/* Label */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
        {isUsed ? (
          <Typography sx={{ ...TEMP_TEXT_BOLD_2XS, color: c.fgLow }}>
            {t('collection.used')}
          </Typography>
        ) : isOwned ? (
          <Box sx={{ bgcolor: TEMP_FG_PRIMARY, borderRadius: '9999px', px: '6px', py: '2px' }}>
            <Typography sx={{ ...TEMP_TEXT_BOLD_2XS, color: '#FFFFFF' }}>
              {t('collection.acquired')}
            </Typography>
          </Box>
        ) : (
          <>
            <Typography sx={{ ...TEMP_TEXT_BOLD_2XS, color: c.fgLower }}>
              {t('collection.locked')}
            </Typography>
            {asset.pieceCost > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                <Icon name="icItem" size={10} color={c.fgLower} />
                <Typography sx={{ ...TEMP_TEXT_BOLD_2XS, color: c.fgLower }}>
                  {asset.pieceCost}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </div>
  );
}

/* ============================================================
   조각 교환 모달
   ============================================================ */
function PieceExchangeModal({
  asset,
  onClose,
  onSuccess,
}: {
  asset: CollectionAssetDto;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t, f } = useTranslator();
  const c = useDarkColors();
  const forcedBlur = useForcedBlur();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const showToast = useToastStore((s) => s.show);

  const { data: pieceBalance } = useQuery({
    queryKey: ['pieceBalance'],
    queryFn: getPieceBalance,
  });

  const canAfford = (pieceBalance?.amount ?? 0) >= asset.pieceCost;

  const imgSrc = forcedBlur
    ? FORCED_BLUR_SRC
    : asset.assetPath
    ? (asset.assetType === 'VIDEO'
        ? getAssetUrl(getVideoPosterPath(asset.assetPath), 'thumb')
        : getAssetUrl(asset.assetPath, 'thumb'))
    : '/images/blur.png';

  const handlePurchase = async () => {
    if (!canAfford || loading) return;
    setLoading(true);
    try {
      const result = await exchangePiece({ itemId: asset.itemId! });
      queryClient.invalidateQueries({ queryKey: ['collection'] });
      queryClient.invalidateQueries({ queryKey: ['collection-detail'] });
      queryClient.invalidateQueries({ queryKey: ['pieceBalance'] });
      showToast('', t('collection.exchange_success'));
      onSuccess();
    } catch (err: any) {
      const code = err?.response?.data?.message;
      showToast('', getErrorMessage(code, t));
      setLoading(false);
    }
  };

  return (
    <>
      {/* 오버레이 */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1800, maxWidth: APP_MAX_WIDTH, margin: '0 auto' }}
      />

      {/* 모달 — 중앙 정렬 래퍼 */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 1801,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
          maxWidth: APP_MAX_WIDTH,
          margin: '0 auto',
        }}
      >
      <div
        style={{
          width: 'calc(100% - 40px)',
          maxWidth: APP_MAX_WIDTH - 40,
          backgroundColor: c.isDark ? '#1a1a1a' : '#fff',
          borderRadius: 24,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 24,
          paddingTop: 32,
          paddingBottom: 20,
          paddingLeft: 20,
          paddingRight: 20,
          pointerEvents: 'auto',
        }}
      >
        {/* 제목 */}
        <Typography sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1.4, color: c.fg, textAlign: 'center', whiteSpace: 'pre-line' }}>
          {t('collection.exchange_title')}
        </Typography>

        {/* 회색 박스 */}
        <Box sx={{ width: '100%', bgcolor: c.isDark ? 'rgba(255,255,255,0.06)' : '#f8f8f8', borderRadius: '16px', p: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px' }}>
          {/* 조각 개수 + 보유량 */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <img src="/svgs/piece.svg" alt="" style={{ width: 28, height: 42, objectFit: 'contain' }} />
              <Typography sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.4, color: c.fg }}>
                {f(t('gacha.piece_count'), [asset.pieceCost])}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 15, fontWeight: 400, lineHeight: 1.4, color: c.fgLow }}>
              {f(t('collection.piece_balance'), [pieceBalance?.amount ?? 0])}
            </Typography>
          </Box>

          {/* 교환 시각화: 조각 카드 → 화살표 → 잠긴 아이템 */}
          <Box sx={{ width: '100%', bgcolor: c.isDark ? 'rgba(255,255,255,0.04)' : '#fff', borderRadius: '12px', py: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
            {/* 조각 카드 */}
            <Box sx={{ position: 'relative', width: 80, height: 80 }}>
              <Box sx={{
                width: '100%', height: '100%',
                bgcolor: '#212121', border: '1.6px solid #616161',
                borderRadius: '4px', overflow: 'hidden',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <img src="/svgs/piece.svg" alt="" style={{ width: 50, height: 50, objectFit: 'contain' }} />
              </Box>
              {/* 개수 배지 */}
              <Box sx={{
                position: 'absolute', bottom: 0, right: 0,
                bgcolor: 'rgba(255,252,252,0.1)', borderTopLeftRadius: '4.8px',
                px: '6px', py: '3px',
              }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, lineHeight: 1.4, color: '#fff' }}>
                  {asset.pieceCost}
                </Typography>
              </Box>
            </Box>

            {/* 화살표 */}
            <Icon name="icArrowRight" size={24} color={c.fg} />

            {/* 잠긴 아이템 카드 */}
            <Box sx={{ position: 'relative', width: 80, height: 80 }}>
              <Box sx={{
                width: '100%', height: '100%',
                bgcolor: '#212121', border: '2px solid #616161',
                borderRadius: '4px', overflow: 'hidden',
              }}>
                <img
                  src={imgSrc}
                  alt=""
                  onError={(e) => { (e.target as HTMLImageElement).src = '/images/blur.png'; }}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'brightness(0.3) blur(20px)' }}
                />
              </Box>
              {/* 자물쇠 */}
              <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)' }}>
                <Icon name="icLock" size={28} color="rgba(255,255,255,0.7)" />
              </Box>
              {/* 보상 타입 배지 */}
              <Box sx={{
                position: 'absolute', bottom: 0, right: 0,
                bgcolor: '#121212', borderTopLeftRadius: '5.7px',
                px: '6.7px', py: '3.8px',
                display: 'flex', gap: '7.6px', alignItems: 'center',
              }}>
                {asset.assetType === 'IMAGE' && <Icon name="icImage" size={11} color="rgba(255,255,255,0.8)" />}
                {asset.assetType === 'VIDEO' && <Icon name="icPlayFill" size={11} color="rgba(255,255,255,0.8)" />}
                {asset.voicePath && <Icon name="icVolume" size={11} color="rgba(255,255,255,0.8)" />}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* 버튼 */}
        <Box sx={{ display: 'flex', gap: '8px', width: '100%' }}>
          <ButtonBase
            onClick={onClose}
            sx={{
              border: `1px solid ${c.border}`, borderRadius: '16px',
              px: '24px', py: '18px', flexShrink: 0,
            }}
          >
            <Typography sx={{ fontSize: 16, fontWeight: 600, lineHeight: 1.4, color: c.fg }}>
              {t('gacha.cancel')}
            </Typography>
          </ButtonBase>
          <ButtonBase
            onClick={handlePurchase}
            disabled={!canAfford || loading}
            sx={{
              bgcolor: canAfford ? TEMP_FG_PRIMARY : '#999',
              borderRadius: '16px', flex: 1,
              px: '10px', py: '18px',
              opacity: loading ? 0.6 : 1,
            }}
          >
            <Typography sx={{ fontSize: 16, fontWeight: 600, lineHeight: 1.4, color: '#fff' }}>
              {loading ? t('gacha.using') : t('gacha.purchase')}
            </Typography>
          </ButtonBase>
        </Box>
      </div>
      </motion.div>
    </>
  );
}
